import { useEffect, useMemo, useState } from 'react'
import {
  DollarSign,
  FileText,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Page } from '@/components/Page'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PostingBanner } from '@/components/PostingBanner'
import { PostingSelectorModal, type PostingSelectorItem } from '@/components/PostingSelectorModal'
import { useUnpostedPreview } from '@/features/reporting/queries'
import { formatDate } from '@/lib/locale'
import { StatCard } from '@/components/StatCard'
import { Combobox, type ComboboxOption } from '@/components/Combobox'
import { Modal } from '@/components/Modal'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { FormField } from '@/components/FormField'
import { DataTable } from '@/components/DataTable'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/useToast'
import { ApiError } from '@/lib/api'
import { formatCurrencyPrecise } from '@/lib/locale'
import { useCoa } from '@/features/coa/queries'
import type { TaxonomyItem } from '@/features/coa/types'
import {
  useAccruals,
  useDeleteAccrual,
  usePostingStatus,
  usePostSelected,
  useSaveAccruals,
} from '@/features/accruals/queries'
import {
  ACCRUAL_TYPES,
  BS_SUGGESTIONS,
  type Accrual,
  type AccrualPayload,
} from '@/features/accruals/types'

function errorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function fmtMoney(v: number | string | null | undefined): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0))
  if (!isFinite(n)) return formatCurrencyPrecise(0)
  return formatCurrencyPrecise(n)
}

/**
 * Form draft used inside <AccrualEditorModal>. Mirrors AccrualPayload but
 * keeps every field as a string for stable controlled-input behaviour.
 * `id` is null for new rows, set for edits.
 */
type AccrualDraft = {
  id: number | null
  date: string
  supplier: string
  accrual_type: string
  accrual_expense_is_account: string
  amount: string
  description: string
  bs_account: string
  department: string
}

function emptyDraft(): AccrualDraft {
  return {
    id: null,
    date: '',
    supplier: '',
    accrual_type: '',
    accrual_expense_is_account: '',
    amount: '',
    description: '',
    bs_account: '',
    department: '',
  }
}

function draftFromAccrual(a: Accrual): AccrualDraft {
  return {
    id: a.id ?? null,
    date: a.date ? String(a.date).slice(0, 10) : '',
    supplier: a.supplier ?? '',
    accrual_type: a.accrual_type ?? '',
    accrual_expense_is_account: a.accrual_expense_is_account ?? '',
    amount: a.amount != null ? String(a.amount) : '',
    description: a.description ?? '',
    bs_account: a.bs_account ?? '',
    department: a.department ?? '',
  }
}

function taxOption(t: TaxonomyItem): ComboboxOption<string> {
  return {
    value: t.account_code,
    label: `${t.account_code} - ${t.subcategory_name}`,
    description: t.main_category,
  }
}

/** Liability B/S values are stored in "<code> - <label>" form (legacy parity). */
function bsValueFor(t: TaxonomyItem): string {
  return `${t.account_code} - ${t.subcategory_name} (${t.main_category})`
}

export function AccrualSchedulePage() {
  const { toast } = useToast()
  const coa = useCoa()
  const accruals = useAccruals()
  const posting = usePostingStatus()
  const saveMut = useSaveAccruals()
  const delMut = useDeleteAccrual()
  const postSelectedMut = usePostSelected()
  const [postSelectorOpen, setPostSelectorOpen] = useState(false)
  const [selectorReadOnly, setSelectorReadOnly] = useState(false)
  const unpostedPreview = useUnpostedPreview(postSelectorOpen)

  // Why: instead of an inline editable grid, accruals open a dedicated
  // modal form for create / edit. editorTarget=null + editorOpen=true
  // means "create a new accrual"; editorTarget set means "edit this one".
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorTarget, setEditorTarget] = useState<Accrual | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Accrual | null>(null)

  const rows = accruals.data ?? []
  const totalAmount = rows.reduce((s, r) => s + (parseFloat(String(r.amount ?? 0)) || 0), 0)
  const unposted = posting.data?.unpostedAccruals ?? 0

  const expenseOptions = useMemo<ComboboxOption<string>[]>(() => {
    return (coa.data ?? [])
      .filter((t) => t.statement_type === 'Income Statement')
      .map(taxOption)
  }, [coa.data])

  const bsOptions = useMemo<ComboboxOption<string>[]>(() => {
    return (coa.data ?? [])
      .filter((t) => t.statement_type === 'Balance Sheet')
      .map((t) => ({
        value: bsValueFor(t),
        label: `${t.account_code} - ${t.subcategory_name}`,
        description: t.main_category,
      }))
  }, [coa.data])

  const bsByCode = useMemo(() => {
    const m = new Map<string, string>()
    for (const t of coa.data ?? []) {
      if (t.statement_type === 'Balance Sheet') m.set(t.account_code, bsValueFor(t))
    }
    return m
  }, [coa.data])

  const openCreate = () => {
    setEditorTarget(null)
    setEditorOpen(true)
  }

  const openEdit = (a: Accrual) => {
    setEditorTarget(a)
    setEditorOpen(true)
  }

  const saveAccrual = async (payload: AccrualPayload) => {
    try {
      await saveMut.mutateAsync([payload])
      toast({
        title: payload.id ? 'Accrual updated.' : 'Accrual saved.',
      })
      setEditorOpen(false)
      setEditorTarget(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Save failed',
        description: errorMessage(err),
      })
      throw err
    }
  }

  const runDelete = async () => {
    if (!deleteTarget?.id) return
    try {
      await delMut.mutateAsync(deleteTarget.id)
      toast({ title: 'Accrual deleted.' })
      setDeleteTarget(null)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Delete failed',
        description: errorMessage(err),
      })
    }
  }

  const accrualItems = useMemo<PostingSelectorItem[]>(() => {
    const previewRows = unpostedPreview.data?.accruals ?? []
    return previewRows.map((r) => ({
      id: r.id,
      cells: [
        r.date ? formatDate(r.date) : '-',
        r.supplier || '-',
        r.accrualType || '-',
        <span className="block max-w-[280px] truncate" title={r.description ?? undefined}>
          {r.description || '-'}
        </span>,
        fmtMoney(r.amount),
      ],
      blocked: false,
    }))
  }, [unpostedPreview.data])

  const accrualColumns = [
    { header: 'Date', width: 'w-32' },
    { header: 'Supplier' },
    { header: 'Type' },
    { header: 'Description' },
    { header: 'Amount', align: 'right' as const, width: 'w-28' },
  ]

  const runPost = async (selection: Record<string, number[]>) => {
    const ids = selection.accruals ?? []
    if (ids.length === 0) return
    try {
      const r = await postSelectedMut.mutateAsync({ source: 'accruals', ids })
      // Why: server returns `posted` as the count of accounting_entries
      // rows it inserted (2× accruals - one expense debit + one liability
      // credit per accrual). Report the user's unit instead so the
      // toast doesn't confusingly say "8" when they posted 4 accruals.
      const ledgerRows = r.posted ?? 0
      const accrualCount = ids.length
      toast({
        title: 'Posted to GL',
        description: `Posted ${accrualCount} accrual${accrualCount === 1 ? '' : 's'} (${ledgerRows} ledger ${ledgerRows === 1 ? 'entry' : 'entries'}).`,
      })
      setPostSelectorOpen(false)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Posting failed',
        description: errorMessage(err),
      })
    }
  }

  const tableColumns = useMemo<ColumnDef<Accrual, unknown>[]>(() => [
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row }) => (row.original.date ? formatDate(row.original.date) : '-'),
    },
    {
      accessorKey: 'supplier',
      header: 'Supplier',
      cell: ({ row }) => row.original.supplier || '-',
    },
    {
      accessorKey: 'accrual_type',
      header: 'Accrual type',
      cell: ({ row }) => row.original.accrual_type || '-',
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className="block max-w-[280px] truncate" title={row.original.description ?? undefined}>
          {row.original.description || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'accrual_expense_is_account',
      header: 'Expense (I/S)',
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.accrual_expense_is_account || '-'}</span>
      ),
    },
    {
      accessorKey: 'bs_account',
      header: 'Liability (B/S)',
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.bs_account || '-'}</span>
      ),
    },
    {
      accessorKey: 'amount',
      header: () => <span className="block text-right">Amount</span>,
      cell: ({ row }) => (
        <span className="block text-right font-mono">{fmtMoney(row.original.amount)}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={(e) => { e.stopPropagation(); openEdit(row.original) }}
            title="Edit accrual"
          >
            <Pencil className="size-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original) }}
            title="Delete accrual"
          >
            <Trash2 className="size-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ], [])

  if (accruals.isLoading || coa.isLoading) {
    return (
      <Page title="Accruals">
        <Card>
          <CardContent className="py-10">
            <LoadingSpinner />
          </CardContent>
        </Card>
      </Page>
    )
  }

  if (accruals.isError) {
    return (
      <Page title="Accruals">
        <Alert variant="destructive">
          <AlertDescription>
            Could not load accruals: {errorMessage(accruals.error)}
          </AlertDescription>
        </Alert>
      </Page>
    )
  }

  return (
    <Page
      title="Accruals"
      description="Record month-end accruals, then post them to the general ledger."
      width="wide"
      actions={
        <Button variant="primary" onClick={openCreate}>
          <Plus className="size-4" /> Add accrual
        </Button>
      }
    >
      <PostingBanner
        count={unposted}
        label="accruals"
        singularLabel="accrual"
        source="accruals"
        onView={() => { setSelectorReadOnly(true); setPostSelectorOpen(true) }}
        onPostNow={() => { setSelectorReadOnly(false); setPostSelectorOpen(true) }}
        busy={postSelectedMut.isPending}
      />

      <PostingSelectorModal
        open={postSelectorOpen}
        onOpenChange={setPostSelectorOpen}
        title={selectorReadOnly ? 'Pending accruals' : 'Post accrual entries'}
        description={selectorReadOnly
          ? 'These accruals are queued to post. Click Post Now to choose which to post.'
          : 'Tick the entries you want to post. Posting only affects the rows you select - bank statements, journal entries, and schedules are untouched.'}
        loading={unpostedPreview.isLoading}
        posting={postSelectedMut.isPending}
        readOnly={selectorReadOnly}
        onSwitchToPost={() => setSelectorReadOnly(false)}
        tabs={[
          {
            key: 'accruals',
            label: 'Accruals',
            items: accrualItems,
            columns: accrualColumns,
            emptyMessage: 'No accruals are pending.',
          },
        ]}
        onPost={runPost}
      />

      {/* Why: dropped the "Unposted" stat - the orange Post Now banner
          above already shows the unposted count, view, and post action
          for this page, so a third card with the same number was
          redundant. Other source pages don't have an Unposted stat
          either, so removing it here keeps the pages consistent. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatCard label="Total accruals" value={rows.length} icon={FileText} />
        <StatCard label="Total amount" value={fmtMoney(totalAmount)} icon={DollarSign} />
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="py-6">
              <EmptyState
                icon={FileText}
                title="No accruals yet"
                description="Add month-end accruals here. Each one becomes a debit (expense) / credit (liability) journal entry when posted."
                action={
                  <Button onClick={openCreate}>
                    <Plus className="size-4" /> Add your first accrual
                  </Button>
                }
              />
            </div>
          ) : (
            <DataTable
              columns={tableColumns}
              rows={rows}
              onRowClick={(r) => openEdit(r)}
              sortable={false}
            />
          )}
        </CardContent>
      </Card>

      <AccrualEditorModal
        open={editorOpen}
        onOpenChange={(v) => { setEditorOpen(v); if (!v) setEditorTarget(null) }}
        target={editorTarget}
        expenseOptions={expenseOptions}
        bsOptions={bsOptions}
        bsByCode={bsByCode}
        coaLoading={coa.isLoading}
        onCoaOpen={() => void coa.refetch()}
        saving={saveMut.isPending}
        onSave={saveAccrual}
      />

      <Modal.Confirm
        open={deleteTarget !== null}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete accrual?"
        body={
          <p>
            This permanently deletes the accrual and removes any posted entries from the ledger.
            Continue?
          </p>
        }
        onConfirm={runDelete}
        onCancel={() => setDeleteTarget(null)}
        busy={delMut.isPending}
      />
    </Page>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Editor modal - create + edit single accrual.
//
// Layout choices:
//  - 3-column grid for the primary fields so it fits on a 1024px viewport
//    without internal scroll. Description is full-width below; department
//    sits in its own narrow row at the bottom.
//  - Mobile (< sm): stacks to one column.
// ──────────────────────────────────────────────────────────────────────────

interface AccrualEditorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = create new; an Accrual = edit. */
  target: Accrual | null
  expenseOptions: ComboboxOption<string>[]
  bsOptions: ComboboxOption<string>[]
  bsByCode: Map<string, string>
  coaLoading: boolean
  onCoaOpen: () => void
  saving: boolean
  onSave: (payload: AccrualPayload) => Promise<void> | void
}

function AccrualEditorModal({
  open,
  onOpenChange,
  target,
  expenseOptions,
  bsOptions,
  bsByCode,
  coaLoading,
  onCoaOpen,
  saving,
  onSave,
}: AccrualEditorModalProps) {
  const [draft, setDraft] = useState<AccrualDraft>(() => emptyDraft())
  const [errors, setErrors] = useState<string[]>([])

  // Reset the form whenever the modal opens (with the target's data for
  // edits, or an empty draft for creates).
  useEffect(() => {
    if (!open) return
    setDraft(target ? draftFromAccrual(target) : emptyDraft())
    setErrors([])
  }, [open, target])

  const update = <K extends keyof AccrualDraft>(key: K, value: AccrualDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  const handleExpenseChange = (code: string | null) => {
    const patch: Partial<AccrualDraft> = { accrual_expense_is_account: code ?? '' }
    if (code && BS_SUGGESTIONS[code] && !draft.bs_account) {
      const bs = bsByCode.get(BS_SUGGESTIONS[code])
      if (bs) patch.bs_account = bs
    }
    setDraft((prev) => ({ ...prev, ...patch }))
  }

  const submit = async () => {
    const errs: string[] = []
    if (!draft.date) errs.push('Date is required.')
    const amt = parseFloat(draft.amount)
    if (!Number.isFinite(amt) || amt <= 0) errs.push('Amount must be greater than zero.')
    if (!draft.accrual_expense_is_account) errs.push('Expense account (I/S) is required - the debit side.')
    if (!draft.bs_account) errs.push('Liability account (B/S) is required - the credit side.')
    if (errs.length > 0) {
      setErrors(errs)
      return
    }
    await onSave({
      id: draft.id,
      date: draft.date,
      supplier: draft.supplier,
      accrual_type: draft.accrual_type,
      accrual_expense_is_account: draft.accrual_expense_is_account,
      amount: draft.amount,
      description: draft.description,
      bs_account: draft.bs_account,
      department: draft.department,
    })
  }

  const accrualTypeOptions = useMemo<ComboboxOption<string>[]>(() => [
    { value: '', label: '- None -' },
    ...ACCRUAL_TYPES.map((t) => ({ value: t.code, label: t.label, description: t.code })),
  ], [])

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={target ? 'Edit accrual' : 'New accrual'}
      className="max-w-3xl"
      footer={
        <div className="flex w-full flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {target ? 'Save changes' : 'Save accrual'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {errors.length > 0 ? (
          <Alert variant="destructive">
            <AlertDescription>
              <ul className="list-disc space-y-0.5 pl-5 text-sm">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        ) : null}

        {/* Row 1: Date · Supplier · Type */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FormField
            label="Date"
            type="date"
            required
            value={draft.date}
            onChange={(e) => update('date', e.currentTarget.value)}
          />
          <FormField
            label="Supplier"
            placeholder="Vendor name…"
            value={draft.supplier}
            onChange={(e) => update('supplier', e.currentTarget.value)}
          />
          <FormField
            label="Accrual type"
            control={
              <Combobox
                options={accrualTypeOptions}
                value={draft.accrual_type || null}
                placeholder="Select type…"
                onChange={(v) => update('accrual_type', v ?? '')}
                popoverClassName="w-[320px] max-w-[90vw]"
              />
            }
          />
        </div>

        {/* Row 2: Amount · Expense (I/S) · Liability (B/S) */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FormField
            label="Amount"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            required
            value={draft.amount}
            onChange={(e) => update('amount', e.currentTarget.value)}
            className="text-right font-mono"
          />
          <FormField
            label="Expense account (I/S)"
            control={
              <Combobox
                options={expenseOptions}
                value={draft.accrual_expense_is_account || null}
                placeholder={coaLoading ? 'Loading…' : 'Search expense account…'}
                onChange={handleExpenseChange}
                onOpen={onCoaOpen}
                maxResults={10000}
                popoverClassName="w-[360px] max-w-[90vw]"
              />
            }
          />
          <FormField
            label="Liability account (B/S)"
            control={
              <Combobox
                options={bsOptions}
                value={draft.bs_account || null}
                placeholder={coaLoading ? 'Loading…' : 'Search liability account…'}
                onChange={(v) => update('bs_account', v ?? '')}
                onOpen={onCoaOpen}
                maxResults={10000}
                popoverClassName="w-[360px] max-w-[90vw]"
              />
            }
          />
        </div>

        {/* Row 3: Description (full width) */}
        <FormField
          label="Description"
          control={
            <Textarea
              rows={2}
              placeholder="What is this for…"
              value={draft.description}
              onChange={(e) => update('description', e.currentTarget.value)}
            />
          }
        />

        {/* Row 4: Department (small) */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FormField
            label="Department"
            placeholder="Optional"
            value={draft.department}
            onChange={(e) => update('department', e.currentTarget.value)}
          />
        </div>
      </div>
    </Modal>
  )
}
